import _isEmpty from 'lodash/isEmpty';
import _merge from 'lodash/merge';
import {
  isValidSecureUrl,
  isValidUrl,
  validateExtraVariables,
  byteLength,
} from '../utilsV3/clientValidatorUtils';
import {
  formatCurrencyNoDecimals,
  normalizeNumber,
} from '../utilsV3/amountUtils';
import { LOGO_TARGET_BUTTON } from '../reducers/reducerButtonFactoryV3';
import { MessageFormat } from '@paypalcorp/worldready';
import { getWorldReady } from '../../utility/worldReadyUtils';

const MAX_TEXT_LENGTH = 127;
const MAX_NOTE_LENGTH = 40;
export const MAX_BUTTON_NAME_LENGTH = 256;

export function validate(values) {
  const worldReady = getWorldReady();

  const amountRequired = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.amount.required',
  }).format();

  const amountMinimumDonationUS = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.amount.minimumDonationUS',
  }).format({ amount: '{amount}' });

  const urlInsecure = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.url.insecure',
  }).format();

  const urlInvalid = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.url.invalid',
  }).format();

  const urlGeneric = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.url.generic',
  }).format();

  const required = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.required',
  }).format();

  const genericSizeError = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.genericSizeError',
  }).format({ maxSize: '{maxSize}' });

  const invalidProgramSizeError = genericSizeError.replace(
    '{maxSize}',
    MAX_TEXT_LENGTH
  );
  const invalidPurposeSizeError = genericSizeError.replace(
    '{maxSize}',
    MAX_TEXT_LENGTH
  );
  const invalidNameSizeError = genericSizeError.replace(
    '{maxSize}',
    MAX_BUTTON_NAME_LENGTH
  );
  const invalidNoteSizeError = genericSizeError.replace(
    '{maxSize}',
    MAX_NOTE_LENGTH
  );

  const restrictedCharacterError = new MessageFormat(worldReady, {
    id: 'setup.shared.error.restrictedCharacter.message',
  }).format();

  const errors = {};

  // Validation for buttonImageUrl
  if (values.usePaypalButtonStyle === 'no') {
    const buttonImageUrl = values.buttonImageUrl;

    if (!buttonImageUrl) {
      errors.buttonImageUrl = required;
    } else if (!isValidUrl(buttonImageUrl)) {
      errors.buttonImageUrl = urlInvalid;
    }
  }

  if (!values.receiverCurrency) {
    errors.receiverCurrency = required;
  }

  const currencyParams = {
    currency: values.receiverCurrency,
    value: 1,
  };
  const minimumDonationError =
    values.receiverCurrency &&
    amountMinimumDonationUS.replace(
      '{amount}',
      formatCurrencyNoDecimals(currencyParams)
    );

  const isUSD = ['USD'].indexOf(values.receiverCurrency) >= 0;

  const validateAmount = (key, amount) => {
    if (_isEmpty(values[key]?.toString())) {
      errors[key] = amountRequired;
    } else if (Number(normalizeNumber(amount)) <= 0) {
      errors[key] = amountRequired;
    } else if (isUSD && Number(normalizeNumber(amount)) < 1) {
      errors[key] = minimumDonationError;
    }
  };

  if (values.amountType === 'fixed') {
    validateAmount('amountValue', values.amountValue);
  }

  if (values.amountType === 'merchant_recommended') {
    validateAmount('merchantAmount1', values.merchantAmount1);
    validateAmount('merchantAmount2', values.merchantAmount2);
    validateAmount('merchantAmount3', values.merchantAmount3);
  }

  if (
    values.logoTarget === LOGO_TARGET_BUTTON &&
    !isValidSecureUrl(values.logoUrl)
  ) {
    errors.logoUrl = urlInsecure;
  }

  if (values.cancelReturn && !isValidUrl(values.cancelReturn)) {
    errors.cancelReturn = urlGeneric;
  }

  if (values.return && !isValidUrl(values.return)) {
    errors.return = urlGeneric;
  }

  const extraVariablesError = validateExtraVariables(values.extraVariablesView);
  if (extraVariablesError) {
    errors.extraVariablesView = extraVariablesError;
  }

  const purposeErrors = validatePurpose(
    values,
    invalidPurposeSizeError,
    restrictedCharacterError
  );
  _merge(errors, purposeErrors);

  if (values.noteLabel && values.noteLabel.length > MAX_NOTE_LENGTH) {
    errors.noteLabel = invalidNoteSizeError;
  }

  if (values.buttonName && values.buttonName.length > MAX_BUTTON_NAME_LENGTH) {
    errors.buttonName = invalidNameSizeError;
  }

  if (values.itemNumber && byteLength(values.itemNumber) > MAX_TEXT_LENGTH) {
    errors.itemNumber = invalidProgramSizeError;
  }

  const programErrors = validatePrograms(values);
  if (!_isEmpty(programErrors)) {
    _merge(errors, programErrors);
  }

  return errors;
}

function validatePurpose(
  values,
  invalidPurposeSizeError,
  restrictedCharacterError
) {
  const errors = {};
  if (values.purpose) {
    if (!/^[^<|>|}|{|\]|\[|`|%|#|"|^|~|\|]+$/.test(values.purpose)) {
      errors.purpose = restrictedCharacterError;
    } else if (byteLength(values.purpose) > MAX_TEXT_LENGTH) {
      errors.purpose = invalidPurposeSizeError;
    }
  }

  return errors;
}

export function validatePrograms(values) {
  const worldReady = getWorldReady();

  const genericSizeError = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.genericSizeError',
  }).format({ maxSize: '{maxSize}' });

  const invalidProgramSizeError = genericSizeError.replace(
    '{maxSize}',
    MAX_TEXT_LENGTH
  );

  const programsAtLeastOne = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.programs.atleastOne',
  }).format();

  const programsDuplicatedProgram = new MessageFormat(worldReady, {
    id: 'buttonsV3.fieldErrors.programs.duplicatedProgram',
  }).format();

  const restrictedCharacterErrorProgram = new MessageFormat(worldReady, {
    id: 'setup.shared.error.restrictedCharacter.message',
  }).format();

  const errors = {};
  if (values.listPrograms === 'no') {
    return;
  }

  const programNames = values.programNames ?? [];

  if (programNames.length > 0) {
    for (let i = 0; i < programNames.length; i++) {
      if (byteLength(programNames[i]) > MAX_TEXT_LENGTH) {
        errors[`programInput${i}`] = invalidProgramSizeError;
      } else if (validateDuplication(programNames[i], i, programNames)) {
        errors[`programInput${i}`] = programsDuplicatedProgram;
      } else if (_isEmpty(programNames[i])) {
        errors[`programInput${i}`] = programsAtLeastOne;
      } else if (
        !/^[^<|>|}|{|\]|\[|`|%|#|"|^|~|\|]+$/.test(programNames[i]) &&
        programNames[i]
      ) {
        errors[`programInput${i}`] = restrictedCharacterErrorProgram;
      }
    }
  }
  return errors;
}

function validateDuplication(value, position, programNames) {
  let duplicated = false;
  for (let i = 0; i < programNames.length; i++) {
    if (value === programNames[i] && position !== i) {
      duplicated = true;
      break;
    }
  }
  return duplicated;
}
